/**
 * ISHVARI WEBSITE V3 — HEADER & NAVIGATION SYSTEM
 * ---------------------------------------------------------------------
 * Vanilla JS only, no dependencies. Loaded once, deferred. Everything
 * is lazily initialised (event delegation + IntersectionObserver, no
 * work done until the relevant markup exists and the user interacts).
 *
 * Sections:
 *   1. Scroll state (transparent-over-hero + shrink), reusing the
 *      hero opt-in hook [data-header-transparent]
 *   2. Desktop mega menu + simple dropdown (hover intent + keyboard)
 *   3. Mobile menu drawer (open/close, focus trap, accordion)
 *   4. Predictive search (Shopify /search/suggest.json, recent
 *      searches via localStorage, debounced)
 *   5. Announcement bar auto-rotate + manual dots
 *   6. Cart badge refresh hook (listens for a "cart:updated" event
 *      that a future cart-drawer component can dispatch)
 * ===================================================================== */

(function () {
  "use strict";

  var header = document.getElementById("SiteHeader");
  if (!header) return;

  var reduceMotion =
    (window.Ishvari && window.Ishvari.animationsDisabled) ||
    (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);

  /* =============================================================
     1. SCROLL STATE — transparent-over-hero + shrink
  ============================================================= */
  (function initScrollState() {
    var transparentEnabled = header.getAttribute("data-transparent-enabled") === "true";
    var hasAltLogo = header.getAttribute("data-has-alt-logo") === "true";

    // Transparent-over-hero requires BOTH the merchant setting AND an
    // alt logo actually uploaded (data-has-alt-logo, set in
    // sections/header.liquid from section.settings.logo_alt) — without
    // an alt logo there is nothing safe to show in place of the default
    // logo, so the effect is skipped entirely and the header stays
    // solid. This mirrors the same [data-has-alt-logo="true"] guard in
    // assets/header.css; keeping both in sync is what prevents the
    // header from ever rendering with an invisible logo.
    if (transparentEnabled && hasAltLogo) {
      // The header only becomes "hero-capable" if the first section on
      // the current page explicitly opts in. This keeps every other
      // template (collection, product, journal…) safely solid without
      // any per-template configuration.
      var main = document.getElementById("MainContent");
      var firstSection = main && main.querySelector(":scope > .shopify-section");
      var heroOptIn = firstSection && firstSection.querySelector("[data-header-transparent]");
      if (heroOptIn) {
        header.classList.add("header--hero-capable");
      }
    }

    var scrollThreshold = 8;
    var ticking = false;

    function applyScrollState() {
      var scrolled = window.scrollY > scrollThreshold;
      header.classList.toggle("is-scrolled", scrolled);
      ticking = false;
    }

    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(applyScrollState);
        ticking = true;
      }
    }

    // Only attach a scroll listener if the header actually needs one:
    // transparent-over-hero, or shrink-on-scroll.
    var needsScrollListener =
      header.classList.contains("header--hero-capable") ||
      header.getAttribute("data-shrink-on-scroll") === "true";

    if (needsScrollListener) {
      applyScrollState();
      window.addEventListener("scroll", onScroll, { passive: true });
    }
  })();

  /* =============================================================
     2. DESKTOP NAVIGATION — mega menu + simple dropdown
  ============================================================= */
  (function initDesktopNav() {
    var navItems = header.querySelectorAll(".site-nav__item.has-mega, .site-nav__item.has-dropdown");
    if (!navItems.length) return;

    var OPEN_DELAY = 80;
    var CLOSE_DELAY = 150;
    var openTimer, closeTimer;

    function getPanel(item) {
      if (item.classList.contains("has-mega")) {
        var toggle = item.querySelector("[data-mega-toggle]");
        var handle = toggle && toggle.getAttribute("data-mega-toggle");
        return handle ? document.getElementById("MegaMenu-" + handle) : null;
      }
      return item.querySelector("[data-dropdown-panel]");
    }

    function openItem(item) {
      clearTimeout(closeTimer);
      openTimer = setTimeout(function () {
        closeAllExcept(item);
        var panel = getPanel(item);
        var toggle = item.querySelector("[data-mega-toggle], [data-dropdown-toggle]");
        item.classList.add("is-open");
        if (toggle) toggle.setAttribute("aria-expanded", "true");
        if (panel) {
          panel.hidden = false;
          // Force layout so the opacity/transform transition runs.
          requestAnimationFrame(function () {
            panel.setAttribute("data-open", "true");
          });
        }
      }, reduceMotion ? 0 : OPEN_DELAY);
    }

    function closeItem(item) {
      clearTimeout(openTimer);
      closeTimer = setTimeout(function () {
        var panel = getPanel(item);
        var toggle = item.querySelector("[data-mega-toggle], [data-dropdown-toggle]");
        item.classList.remove("is-open");
        if (toggle) toggle.setAttribute("aria-expanded", "false");
        if (panel) {
          panel.removeAttribute("data-open");
          window.setTimeout(function () {
            if (!item.classList.contains("is-open")) panel.hidden = true;
          }, reduceMotion ? 0 : 250);
        }
      }, reduceMotion ? 0 : CLOSE_DELAY);
    }

    function closeAllExcept(exceptItem) {
      navItems.forEach(function (item) {
        if (item !== exceptItem && item.classList.contains("is-open")) {
          closeItem(item);
        }
      });
    }

    navItems.forEach(function (item) {
      item.addEventListener("mouseenter", function () { openItem(item); });
      item.addEventListener("mouseleave", function () { closeItem(item); });

      var toggle = item.querySelector("[data-mega-toggle], [data-dropdown-toggle]");
      if (toggle) {
        toggle.addEventListener("click", function (e) {
          e.preventDefault();
          if (item.classList.contains("is-open")) {
            closeItem(item);
          } else {
            openItem(item);
          }
        });
      }

      item.addEventListener("focusin", function () { openItem(item); });
      item.addEventListener("focusout", function (e) {
        if (!item.contains(e.relatedTarget)) closeItem(item);
      });
    });

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape") {
        navItems.forEach(function (item) {
          if (item.classList.contains("is-open")) {
            closeItem(item);
            var link = item.querySelector(".site-nav__link");
            if (link) link.focus();
          }
        });
      }
    });

    document.addEventListener("click", function (e) {
      navItems.forEach(function (item) {
        if (item.classList.contains("is-open") && !item.contains(e.target)) {
          closeItem(item);
        }
      });
    });
  })();

  /* =============================================================
     3. MOBILE MENU — open/close, focus trap, accordion

     FIXES (added 2026-08-24, audit pass — no markup/CSS changed):
     a) Rapid close-then-reopen race: closeMenu() schedules a delayed
        `menu.hidden = true` (so the CSS slide-out transition can
        play). If a shopper tapped the hamburger again before that
        timer fired, the stale timer still ran afterward and hid an
        already-reopened, visually-open menu. Now tracked in
        `hideTimer` and cleared at the top of openMenu().
     b) Accordion submenu links stayed in the Tab order while their
        panel was visually collapsed (max-height: 0 in header.css
        clips them, but doesn't remove keyboard focusability) — a
        sighted keyboard user could Tab into invisible links. Panel
        links now get tabindex="-1" while collapsed and have it
        removed when their accordion opens, matching the standard
        accordion accessibility pattern.
  ============================================================= */
  (function initMobileMenu() {
    var menu = document.getElementById("MobileMenu");
    var openButton = header.querySelector("[data-mobile-menu-toggle]");
    if (!menu || !openButton) return;

    var closeTriggers = menu.querySelectorAll("[data-mobile-menu-close]");
    var lastFocused = null;
    var hideTimer = null;

    function focusableElements() {
      return menu.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
    }

    function openMenu() {
      clearTimeout(hideTimer);
      lastFocused = document.activeElement;
      menu.hidden = false;
      requestAnimationFrame(function () {
        menu.setAttribute("data-open", "true");
      });
      document.documentElement.classList.add("js-scroll-lock");
      openButton.setAttribute("aria-expanded", "true");
      var focusables = focusableElements();
      if (focusables.length) focusables[0].focus();
      document.addEventListener("keydown", onKeydown);
    }

    function closeMenu() {
      menu.removeAttribute("data-open");
      document.documentElement.classList.remove("js-scroll-lock");
      openButton.setAttribute("aria-expanded", "false");
      document.removeEventListener("keydown", onKeydown);
      clearTimeout(hideTimer);
      hideTimer = window.setTimeout(function () {
        menu.hidden = true;
      }, reduceMotion ? 0 : 400);
      if (lastFocused) lastFocused.focus();
    }

    function onKeydown(e) {
      if (e.key === "Escape") {
        closeMenu();
        return;
      }
      if (e.key !== "Tab") return;

      var focusables = Array.prototype.slice.call(focusableElements());
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    openButton.addEventListener("click", openMenu);
    closeTriggers.forEach(function (el) {
      el.addEventListener("click", closeMenu);
    });

    // Accordion — one panel open at a time within the drawer. Panel
    // links start non-focusable (collapsed by default) so a keyboard
    // user can't Tab into a visually-hidden submenu.
    var accordionToggles = menu.querySelectorAll("[data-accordion-toggle]");
    accordionToggles.forEach(function (toggle) {
      var item = toggle.closest(".mobile-nav__item");
      var panel = item && item.querySelector("[data-accordion-panel]");
      if (panel) {
        panel.querySelectorAll("a").forEach(function (link) {
          link.setAttribute("tabindex", "-1");
        });
      }

      toggle.addEventListener("click", function () {
        var isOpen = item.classList.contains("is-open");
        item.classList.toggle("is-open", !isOpen);
        toggle.setAttribute("aria-expanded", String(!isOpen));

        if (panel) {
          panel.querySelectorAll("a").forEach(function (link) {
            if (isOpen) {
              link.setAttribute("tabindex", "-1");
            } else {
              link.removeAttribute("tabindex");
            }
          });
        }
      });
    });
  })();

  /* =============================================================
     4. PREDICTIVE SEARCH — native Shopify /search/suggest.json
  ============================================================= */
  (function initPredictiveSearch() {
    var panel = document.getElementById("PredictiveSearch");
    var toggle = header.querySelector("[data-search-toggle]");
    if (!panel || !toggle) return;

    var input = panel.querySelector("[data-predictive-search-input]");
    var resultsEl = panel.querySelector("[data-predictive-results]");
    var recentWrap = panel.querySelector("[data-predictive-recent]");
    var recentChips = panel.querySelector("[data-recent-chips]");
    var recentClear = panel.querySelector("[data-recent-clear]");
    var closeButtons = panel.querySelectorAll("[data-search-close]");
    var RECENT_KEY = "ishvari:recent-searches";
    var debounceTimer;
    var currentController;

    function getRecent() {
      try {
        return JSON.parse(window.localStorage.getItem(RECENT_KEY) || "[]");
      } catch (e) {
        return [];
      }
    }

    function saveRecent(term) {
      if (!term) return;
      try {
        var list = getRecent().filter(function (t) { return t.toLowerCase() !== term.toLowerCase(); });
        list.unshift(term);
        window.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 5)));
      } catch (e) {
        /* localStorage unavailable (private mode) — recent search history simply won't persist. */
      }
    }

    function renderRecent() {
      var list = getRecent();
      if (!recentWrap || !recentChips) return;
      if (!list.length) {
        recentWrap.hidden = true;
        return;
      }
      recentWrap.hidden = false;
      recentChips.innerHTML = "";
      list.forEach(function (term) {
        var chip = document.createElement("button");
        chip.type = "button";
        chip.className = "predictive-search__recent-chip text-small";
        chip.textContent = term;
        chip.addEventListener("click", function () {
          input.value = term;
          runSearch(term);
        });
        recentChips.appendChild(chip);
      });
    }

    function escapeHtml(str) {
      return (str || "").replace(/[&<>"']/g, function (c) {
        return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
      });
    }

    function renderResults(data, term) {
      if (!resultsEl) return;
      var resources = data.resources && data.resources.results ? data.resources.results : {};
      var products = resources.products || [];
      var collections = resources.collections || [];
      var articles = resources.articles || [];
      var pages = resources.pages || [];

      if (!products.length && !collections.length && !articles.length && !pages.length) {
        resultsEl.innerHTML =
          '<p class="predictive-search__empty text-body">No results for “' + escapeHtml(term) + '”.</p>';
        return;
      }

      var html = "";

      if (products.length) {
        html += '<div class="predictive-search__group">';
        html += '<p class="predictive-search__group-title text-label">Products</p>';
        html += '<div class="predictive-search__result-list">';
        products.forEach(function (p) {
          html +=
            '<a class="predictive-search__result-card" href="' + p.url + '">' +
            (p.image
              ? '<span class="aspect-square"><img src="' + p.image + '" alt="' + escapeHtml(p.title) + '" width="200" height="200" loading="lazy"></span>'
              : "") +
            '<span class="text-small text-weight-medium">' + escapeHtml(p.title) + "</span>" +
            (p.price ? '<span class="text-small text-muted">' + p.price + "</span>" : "") +
            "</a>";
        });
        html += "</div></div>";
      }

      if (collections.length) {
        html += '<div class="predictive-search__group">';
        html += '<p class="predictive-search__group-title text-label">Collections</p>';
        html += '<div class="predictive-search__link-list">';
        collections.forEach(function (c) {
          html += '<a class="predictive-search__result-link text-body" href="' + c.url + '">' + escapeHtml(c.title) + "</a>";
        });
        html += "</div></div>";
      }

      if (articles.length) {
        html += '<div class="predictive-search__group">';
        html += '<p class="predictive-search__group-title text-label">Journal</p>';
        html += '<div class="predictive-search__link-list">';
        articles.forEach(function (a) {
          html += '<a class="predictive-search__result-link text-body" href="' + a.url + '">' + escapeHtml(a.title) + "</a>";
        });
        html += "</div></div>";
      }

      // Pages cover any Ingredients content the merchant has published as
      // a Shopify Page. Predictive Search does not query custom
      // metaobjects (e.g. a dedicated Ingredients metaobject) — that
      // would require the Storefront API and is out of scope for
      // native Predictive Search.
      if (pages.length) {
        html += '<div class="predictive-search__group">';
        html += '<p class="predictive-search__group-title text-label">Pages</p>';
        html += '<div class="predictive-search__link-list">';
        pages.forEach(function (pg) {
          html += '<a class="predictive-search__result-link text-body" href="' + pg.url + '">' + escapeHtml(pg.title) + "</a>";
        });
        html += "</div></div>";
      }

      resultsEl.innerHTML = html;
    }

    function runSearch(term) {
      if (currentController) currentController.abort();
      if (!term || term.length < 2) {
        resultsEl.innerHTML = "";
        renderRecent();
        return;
      }
      if (recentWrap) recentWrap.hidden = true;

      currentController = new AbortController();
      var url =
        window.Shopify && window.Shopify.routes && window.Shopify.routes.root
          ? window.Shopify.routes.root + "search/suggest.json"
          : "/search/suggest.json";
      var params =
        "?q=" + encodeURIComponent(term) +
        "&resources[type]=product,collection,page,article" +
        "&resources[limit]=4" +
        "&resources[limit_scope]=each" +
        "&resources[options][fields]=title,product_type,variants.title,vendor";

      fetch(url + params, { signal: currentController.signal })
        .then(function (res) { return res.json(); })
        .then(function (data) {
          renderResults(data, term);
          saveRecent(term);
        })
        .catch(function (err) {
          if (err.name !== "AbortError") {
            resultsEl.innerHTML = '<p class="predictive-search__empty text-body">Search is temporarily unavailable.</p>';
          }
        });
    }

    function openSearch() {
      panel.hidden = false;
      requestAnimationFrame(function () { panel.setAttribute("data-open", "true"); });
      toggle.setAttribute("aria-expanded", "true");
      document.documentElement.classList.add("js-scroll-lock");
      renderRecent();
      window.setTimeout(function () { input.focus(); }, 50);
      document.addEventListener("keydown", onKeydown);
    }

    function closeSearch() {
      panel.removeAttribute("data-open");
      toggle.setAttribute("aria-expanded", "false");
      document.documentElement.classList.remove("js-scroll-lock");
      document.removeEventListener("keydown", onKeydown);
      window.setTimeout(function () { panel.hidden = true; }, reduceMotion ? 0 : 250);
    }

    function onKeydown(e) {
      if (e.key === "Escape") closeSearch();
    }

    toggle.addEventListener("click", function () {
      var isOpen = toggle.getAttribute("aria-expanded") === "true";
      isOpen ? closeSearch() : openSearch();
    });
    closeButtons.forEach(function (btn) { btn.addEventListener("click", closeSearch); });

    input.addEventListener("input", function () {
      clearTimeout(debounceTimer);
      var term = input.value.trim();
      debounceTimer = setTimeout(function () { runSearch(term); }, 250);
    });

    if (recentClear) {
      recentClear.addEventListener("click", function () {
        try { window.localStorage.removeItem(RECENT_KEY); } catch (e) {}
        renderRecent();
      });
    }
  })();

  /* =============================================================
     5. ANNOUNCEMENT BAR — auto-rotate + manual dots
  ============================================================= */
  (function initAnnouncementBar() {
    var bar = document.getElementById("AnnouncementBar");
    if (!bar) return;

    var items = bar.querySelectorAll("[data-announcement-item]");
    var dots = bar.querySelectorAll("[data-announcement-dot]");
    if (items.length < 2) return;

    var current = 0;
    var autoRotate = bar.getAttribute("data-autorotate") === "true" && !reduceMotion;
    var speed = (parseInt(bar.getAttribute("data-autorotate-speed"), 10) || 5) * 1000;
    var timer;

    function show(index) {
      items[current].classList.remove("is-active");
      if (dots[current]) {
        dots[current].classList.remove("is-active");
        dots[current].setAttribute("aria-selected", "false");
      }
      current = index;
      items[current].classList.add("is-active");
      if (dots[current]) {
        dots[current].classList.add("is-active");
        dots[current].setAttribute("aria-selected", "true");
      }
    }

    function next() {
      show((current + 1) % items.length);
    }

    function startTimer() {
      if (!autoRotate) return;
      clearInterval(timer);
      timer = setInterval(next, speed);
    }

    dots.forEach(function (dot, i) {
      dot.addEventListener("click", function () {
        show(i);
        startTimer();
      });
    });

    bar.addEventListener("mouseenter", function () { clearInterval(timer); });
    bar.addEventListener("mouseleave", startTimer);

    startTimer();
  })();

  /* =============================================================
     6. CART BADGE — refresh hook for a future cart-drawer component
  ============================================================= */
  (function initCartBadge() {
    var badge = header.querySelector("[data-cart-count]");
    if (!badge) return;

    document.addEventListener("cart:updated", function (e) {
      var count = e.detail && typeof e.detail.itemCount === "number" ? e.detail.itemCount : null;
      if (count === null) return;
      badge.textContent = String(count);
      badge.classList.toggle("is-hidden", count === 0);
    });
  })();

  /* =============================================================
     7. CART DRAWER COMPATIBILITY
     No cart drawer exists yet in this phase. If/when one is built,
     it only needs to define a custom element/component matching the
     selector below and listen for this click — the header requires
     no changes. Until then the icon simply navigates to /cart.
  ============================================================= */
  (function initCartDrawerHook() {
    var cartIcon = header.querySelector("[data-cart-icon]");
    if (!cartIcon) return;

    cartIcon.addEventListener("click", function (e) {
      var drawer = document.querySelector("[data-cart-drawer]");
      if (drawer && typeof drawer.open === "function") {
        e.preventDefault();
        drawer.open();
      }
      // No drawer present: default navigation to routes.cart_url proceeds.
    });
  })();
})();
